import { gql } from 'graphql-request';

import { requestWithAuth } from '@/utilities/graphQLClient';

import {
  WATCHLIST_ITEM_FRAGMENT,
  WATCHLIST_NARRATIVE_FAIR_VALUE_FRAGMENT,
} from '@/hooks/useWatchlist';
import type {
  WatchlistFragmentType,
  WatchlistItemFragmentType,
} from '@/hooks/useWatchlist';

interface Error {
  message: string;
}

interface RenameWatchlistResponse {
  errors: Error[];
  renameWatchlist: {
    id: WatchlistFragmentType['id'];
    name: WatchlistFragmentType['name'];
  };
}

interface DeleteWatchlistResponse {
  deleteWatchlist: boolean;
  errors: Error[];
}

interface AddWatchlistItemResponse {
  addWatchlistItem: WatchlistItemFragmentType;
  errors: Error[];
}

interface RemoveCompanyResponse {
  removeWatchlistItem: boolean;
  errors: Error[];
}

export interface AddWatchlistItemPayload {
  id: string;
  watchlistId: string;
  includeSnackBar?: boolean;
  autoAdded?: boolean;
  includeWatchlistFairValue?: boolean;
}

export interface RemoveWatchlistItemPayload {
  id: string;
  watchlistId: string;
  includeSnackBar?: boolean;
  message?: string;
}

export interface RenameWatchlistPayload {
  id: string;
  newName: string;
}

export interface DeleteWatchlistPayload {
  id: string;
}

export const addQueryFn = async (
  payload: AddWatchlistItemPayload
): Promise<AddWatchlistItemResponse['addWatchlistItem']> => {
  try {
    const response = await requestWithAuth<AddWatchlistItemResponse>(
      gql`
        mutation addWatchlistItem(
          $id: String!
          $watchlistId: String!
          $autoAdded: Boolean
          $includeWatchlistFairValue: Boolean!
        ) {
          addWatchlistItem(
            id: $id
            watchlistId: $watchlistId
            autoAdded: $autoAdded
          ) {
            ...watchlistItemFields
            ...narrativeFairValueFields @include(if: $includeWatchlistFairValue)
          }
        }
        ${WATCHLIST_ITEM_FRAGMENT}
        ${WATCHLIST_NARRATIVE_FAIR_VALUE_FRAGMENT}
      `,
      {
        id: payload.id,
        watchlistId: payload.watchlistId,
        autoAdded: payload.autoAdded,
        includeWatchlistFairValue: payload.includeWatchlistFairValue,
      }
    );

    const { addWatchlistItem, errors } = response;
    if (errors && errors.length > 0) {
      throw new Error(errors[0].message);
    }
    return addWatchlistItem;
  } catch (error) {
    throw error;
  }
};

export const removeQueryFn = async (
  payload: RemoveWatchlistItemPayload
): Promise<boolean> => {
  try {
    const response = await requestWithAuth<RemoveCompanyResponse>(
      gql`
        mutation removeWatchlistItem(
          $companyId: String!
          $watchlistId: String!
        ) {
          removeWatchlistItem(companyId: $companyId, watchlistId: $watchlistId)
        }
      `,
      {
        companyId: payload.id,
        watchlistId: payload.watchlistId,
      }
    );
    const { removeWatchlistItem, errors } = response;
    if (errors && errors.length > 0) {
      throw new Error(errors[0].message);
    }
    return removeWatchlistItem;
  } catch (error) {
    console.log(error);
  }
  return false;
};

export const renameWatchlistQueryFn = async (
  payload: RenameWatchlistPayload
): Promise<RenameWatchlistResponse['renameWatchlist']> => {
  try {
    const response = await requestWithAuth<RenameWatchlistResponse>(
      gql`
        mutation renameWatchlist($id: String!, $newName: String!) {
          renameWatchlist(id: $id, newName: $newName) {
            id
            name
          }
        }
      `,
      {
        id: payload.id,
        newName: payload.newName,
      }
    );
    const { renameWatchlist, errors } = response;
    if (errors && errors.length > 0) {
      throw new Error(errors[0].message);
    }
    return renameWatchlist;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteWatchlistQueryFn = async (
  payload: DeleteWatchlistPayload
): Promise<boolean> => {
  try {
    const response = await requestWithAuth<DeleteWatchlistResponse>(
      gql`
        mutation deleteWatchlist($id: String!) {
          deleteWatchlist(id: $id)
        }
      `,
      {
        id: payload.id,
      }
    );
    const { deleteWatchlist, errors } = response;
    if (errors && errors.length > 0) {
      throw new Error(errors[0].message);
    }
    return deleteWatchlist;
  } catch (error) {
    console.error(error);
  }
  return false;
};
